// RemoteCoalesceQueue — backpressure for **remote** (peer) drawing broadcasts.
//
// Why this exists (docs/_wip/backpressure-coalesce.md, consult 2026-05-23):
//
//   The pre-2026-05-23 engine gated worker-bound `stroke.points` per rAF tick
//   and **silent-dropped** the overflow. `stroke.start`/`end` bypassed the
//   gate, so dropping a middle `stroke.points` left the renderer connecting
//   the surviving points with a straight line — a curve collapses to a chord.
//   In a backgrounded tab the browser throttles `setTimeout` to ~1/sec while
//   peers keep sending ~60 segments/sec, so the budget almost never reopened
//   and the vast majority of inbound points were dropped → received art was
//   corrupted (弦化), not merely stuttered.
//
//   The gate's *real* goal is only to bound the **number / size of postMessages**
//   to the drawing worker, not to discard data. Same-sid `stroke.points` are
//   simply concatenated by the renderer (it keeps per-sid interpolation state
//   across `addPoints` calls — verified in strokeRenderer.ts), so we can MERGE
//   the `points` arrays of several queued segments into one postMessage with
//   **bit-identical render output**. That is exactly what this queue does:
//   coalesce instead of drop. No point is ever lost under normal load.
//
// Local strokes do NOT go through this queue — see CanvasDrawingEngine: local
// `addPoints` is already self-rate-limited to ~1 flush/frame by rAF and can't
// flood, and dropping/decimating one's own line is the worst possible UX.
// This queue is solely for the flood-prone remote (`applyRemoteStroke`) path.
//
// ─── Ordering contract ──────────────────────────────────────────────────────
//
// "Ordered queue + adjacent same-sid points-merge + everything else is a
// barrier":
//
//   - `stroke.points(sid)`: if the queue tail is a points-op for the SAME sid,
//     append the points there (merge). Otherwise push a new points-op. Merging
//     only adjacent ops means an interleaved op for a different sid (or any
//     barrier) splits the run, so enqueue order — and therefore forward order
//     — is preserved exactly.
//   - `stroke.start` / `stroke.end` / `clear` / `imagepaste.*` / everything
//     else: a **barrier**. Pushed verbatim, never merged, order kept. Because
//     a `stroke.end(sid)` is enqueued after that sid's points-ops, flushing the
//     queue front-to-back guarantees end never overtakes its own points. A
//     `clear` likewise flushes everything enqueued before it before it forwards,
//     and points that arrive after a clear land in a fresh points-op = correctly
//     drawn post-clear.
//
// Flush walks the queue front-to-back and forwards every op via the injected
// `forward` callback (= the engine's worker postMessage path).
//
// ─── Absolute cap + decimation ───────────────────────────────────────────────
//
// A pathological producer (or a tab backgrounded long enough that even the
// throttled timeout barely fires) could grow the queue without bound. We cap
// the total buffered point count; on overflow we DECIMATE (thin the largest
// points-op by keeping every other point) rather than drop. Decimation keeps
// the stroke's *shape* — it just renders it coarser — whereas dropping a
// contiguous run reintroduces the chord-collapse corruption we set out to kill.
// Under realistic load (even backgrounded) the cap is never reached; it is a
// memory-safety net, not a normal-path mechanism.

import type { StrokePoint } from '@oekaki/protocol';
import type { EngineWorkerInbound } from './worker/workerProtocol.js';

/** A coalescable run of same-sid points awaiting flush. */
interface PointsOp {
  readonly kind: 'points';
  readonly sid: string;
  /** Mutable: adjacent same-sid `stroke.points` append here. */
  points: StrokePoint[];
  /** `nowMs` of the most recent merged segment (carried to the renderer). */
  nowMs: number;
}

/** Any non-`stroke.points` message — forwarded verbatim, never merged. */
interface BarrierOp {
  readonly kind: 'barrier';
  readonly msg: EngineWorkerInbound;
}

type QueueOp = PointsOp | BarrierOp;

/**
 * Absolute upper bound on the number of remote points buffered across all
 * pending points-ops before decimation kicks in.
 *
 * (要検証, backpressure-coalesce.md §要検証 2): sized as
 *   background timer throttle floor (~1 s) × peer send rate (~60 pts/s) × peers
 * with generous headroom. At ~60 pts/s/peer and a worst-case multi-second
 * background stall across several peers we still want to buffer everything, so
 * we set the net well above any realistic accumulation. 50 000 points ≈ a few
 * MB of `{x,y,p,pAlpha}` objects — comfortably bounded, and far past the point
 * where a normal foreground/background flush would have drained the queue.
 *
 * Tune from real-device telemetry (background throttle floor is the dominant
 * unknown). Until then this is purely a memory-safety net that realistic load
 * never reaches.
 */
export const REMOTE_COALESCE_POINT_CAP = 50_000;

/**
 * Ordered, coalescing buffer for remote drawing broadcasts bound for the
 * drawing worker. See file header for the full rationale + ordering contract.
 *
 * The queue is transport-agnostic: it never calls `postMessage` itself. The
 * owner injects a `forward` callback (the engine's worker-send path) and calls
 * `flush()` from its rAF / timeout tick.
 */
export class RemoteCoalesceQueue {
  private ops: QueueOp[] = [];
  /** Running total of points across all pending points-ops (cap accounting). */
  private bufferedPointCount = 0;

  // ─── metrics (tests / debug) ───────────────────────────────────────────────
  /** Total points removed by decimation over this queue's lifetime. */
  private decimatedPointCount = 0;
  /** Number of postMessages emitted by `flush` over this queue's lifetime. */
  private coalescedMsgCount = 0;

  constructor(
    private readonly forward: (msg: EngineWorkerInbound) => void,
    private readonly pointCap: number = REMOTE_COALESCE_POINT_CAP,
  ) {}

  /**
   * Enqueue a `stroke.points` run. Merges into the tail op iff the tail is a
   * points-op for the same sid (adjacency rule — see header). Triggers
   * decimation if the buffered point total exceeds the cap.
   */
  enqueuePoints(sid: string, points: readonly StrokePoint[], nowMs: number): void {
    if (points.length === 0) return;
    const tail = this.ops[this.ops.length - 1];
    if (tail !== undefined && tail.kind === 'points' && tail.sid === sid) {
      // Adjacent same-sid run → merge (the renderer concatenates these into
      // one continuous stroke, so the merge is render-identical).
      for (const pt of points) tail.points.push(pt);
      tail.nowMs = nowMs;
    } else {
      this.ops.push({ kind: 'points', sid, points: [...points], nowMs });
    }
    this.bufferedPointCount += points.length;
    if (this.bufferedPointCount > this.pointCap) {
      this.decimateToCap();
    }
  }

  /**
   * Enqueue any non-points message as a barrier. Pushed verbatim so enqueue
   * order is preserved on flush. `stroke.end` ordering (end never overtakes
   * its own points) and `clear` ordering (clear flushes everything before it)
   * both fall out of "barriers keep their place + flush is front-to-back".
   */
  enqueueBarrier(msg: EngineWorkerInbound): void {
    this.ops.push({ kind: 'barrier', msg });
  }

  /**
   * Forward every queued op, front-to-back, then empty the queue. Points-ops
   * become a single `stroke.points` postMessage carrying the merged run;
   * barriers forward their original message. Called from the engine's rAF /
   * timeout flush tick.
   */
  flush(): void {
    if (this.ops.length === 0) return;
    const ops = this.ops;
    this.ops = [];
    this.bufferedPointCount = 0;
    for (const op of ops) {
      if (op.kind === 'points') {
        this.forward({ t: 'stroke.points', sid: op.sid, points: op.points, nowMs: op.nowMs });
      } else {
        this.forward(op.msg);
      }
      this.coalescedMsgCount++;
    }
  }

  /** True when nothing is pending (engine skips arming a flush tick). */
  isEmpty(): boolean {
    return this.ops.length === 0;
  }

  /** Drop all pending ops without forwarding (dispose path). */
  clear(): void {
    this.ops = [];
    this.bufferedPointCount = 0;
  }

  /** Test / debug — points removed by decimation over the queue's lifetime. */
  getDecimatedPointCount(): number {
    return this.decimatedPointCount;
  }

  /** Test / debug — postMessages emitted by flush over the queue's lifetime. */
  getCoalescedMsgCount(): number {
    return this.coalescedMsgCount;
  }

  /** Test / debug — points currently buffered across all pending points-ops. */
  getBufferedPointCount(): number {
    return this.bufferedPointCount;
  }

  /**
   * Bring `bufferedPointCount` back under the cap by thinning the largest
   * points-op(s). We halve a run by keeping every other point — the first and
   * (because we keep even indices and runs are long) effectively the last
   * point survive, so the stroke's overall path is preserved at coarser
   * resolution. We never delete a whole op (= never reintroduce the chord
   * corruption); we only reduce sample density.
   *
   * Repeated until back under cap, always targeting the current largest op so
   * the visual degradation is spread toward the heaviest strokes first.
   */
  private decimateToCap(): void {
    while (this.bufferedPointCount > this.pointCap) {
      // Find the largest points-op (most to gain, least relative shape loss).
      let largest: PointsOp | null = null;
      for (const op of this.ops) {
        if (op.kind !== 'points') continue;
        if (largest === null || op.points.length > largest.points.length) {
          largest = op;
        }
      }
      // No points-op left to thin (queue is all barriers) — can't reduce
      // further without dropping a barrier, which we never do. Bail out;
      // barriers are tiny and bounded by event cadence anyway.
      if (largest === null || largest.points.length <= 2) break;
      const before = largest.points.length;
      // Keep even indices (every other point). Index 0 always survives, and
      // for an even-length run the last index is odd → dropped; for odd it
      // survives. Either way the head + bulk of the path is retained.
      const thinned: StrokePoint[] = [];
      for (let i = 0; i < largest.points.length; i += 2) {
        const pt = largest.points[i];
        if (pt !== undefined) thinned.push(pt);
      }
      largest.points = thinned;
      const removed = before - thinned.length;
      this.bufferedPointCount -= removed;
      this.decimatedPointCount += removed;
    }
  }
}
